// Phase T2.0A Prompt 2 (hotfix) — Redesigned Round Robin create dialog.
//
// Sections: Basic Information (Name, Description, Flyer) → Event Setup (Type,
// Composition, Pools, Capacity, Final?) → Date, Location & Format → Manager.
//
// Flyer is picked locally then uploaded after the round-robin is created.
//
// E2 (Jun 2026): `embedded` prop renders the same form on the unified
// "+ New Event" full-screen page instead of a modal. Zero logic change.

import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter,
  DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { roundRobinAPI } from '../../lib/roundRobinApi';
import { adminAPI } from '../../lib/api';
import EventDetailsFieldset from '../events/EventDetailsFieldset';
import EventFlyerLocalPicker, { validateFlyerFile } from '../events/EventFlyerLocalPicker';
import ManagerSelectField from '../events/ManagerSelectField';

const label = 'text-[11px] uppercase tracking-wider text-[#6a766e]';
const input = 'mt-1 w-full px-3 py-2 border border-[#e3dcd1] rounded-lg text-sm min-h-[44px] focus:outline-none focus:border-[#015e3f] focus:ring-2 focus:ring-[#015e3f]/20';

const EMPTY_DETAILS = {
  description: '', location_text: '',
  starts_at: null, ends_at: null,
  entry_fee_cents: null,
  payment_type: null, payment_link: null,
  match_format_text: '',
};

export default function CreateRoundRobinDialog({ open, onClose, onCreated, embedded = false }) {
  const [name, setName] = useState('');
  const [rrType, setRrType] = useState('doubles');
  const [composition, setComposition] = useState('open');
  const [numPools, setNumPools] = useState(2);
  const [hasFinal, setHasFinal] = useState(true);
  const [maxEntries, setMaxEntries] = useState('');
  const [details, setDetails] = useState(EMPTY_DETAILS);
  const [flyerFile, setFlyerFile] = useState(null);
  const [managerProfileId, setManagerProfileId] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setName(''); setRrType('doubles'); setComposition('open');
    setNumPools(2); setHasFinal(true); setMaxEntries('');
    setDetails(EMPTY_DETAILS); setFlyerFile(null);
    setManagerProfileId(null);
  };

  const submit = async () => {
    if (busy) return;
    if (!name.trim()) return toast.error('Name is required.');
    if (rrType === 'singles' && composition === 'mixed') {
      return toast.error('Mixed composition is only valid for doubles.');
    }
    if (flyerFile) {
      const err = validateFlyerFile(flyerFile);
      if (err) return toast.error(err);
    }
    setBusy(true);
    try {
      const payload = {
        name: name.trim(),
        rr_type: rrType,
        composition,
        num_pools: numPools,
        has_final: hasFinal,
        max_entries: maxEntries ? Number(maxEntries) : null,
        description: details.description || null,
        location_text: details.location_text || null,
        starts_at: details.starts_at || null,
        ends_at: details.ends_at || null,
        entry_fee_cents: details.entry_fee_cents,
        payment_type: details.payment_type || null,
        payment_link: details.payment_link || null,
        match_format_text: details.match_format_text || null,
      };
      const created = (await roundRobinAPI.create(payload)).data;
      if (flyerFile) {
        try {
          await roundRobinAPI.uploadFlyer(created.id, flyerFile);
        } catch (e) {
          const detail = e.response?.data?.detail;
          const msg = typeof detail === 'string' ? detail : (detail?.message || 'Flyer upload failed.');
          toast.warning(`Round robin created, but the flyer did not upload: ${msg}. Add it from Manage.`);
        }
      }
      if (managerProfileId) {
        try {
          await adminAPI.assignEventManager('round_robin', created.id, managerProfileId);
        } catch (e) {
          const detail = e.response?.data?.detail;
          const msg = typeof detail === 'string' ? detail : (detail?.message || 'Assignment failed.');
          toast.warning(`Event created, but Event Manager could not be assigned: ${msg}. You can retry from Manage.`);
        }
      }
      toast.success('Round robin created.');
      onCreated?.(created);
      reset();
      if (!embedded) onClose?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Could not create round robin.'));
    } finally { setBusy(false); }
  };

  const sections = (
    <>
      <section className="space-y-3">
        <div className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold">Basic Information</div>
        <label className="block">
          <span className={label}>Event name</span>
          <input
            data-testid="create-rr-name"
            autoFocus type="text" value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Wimbledon White Out — July 18"
            className={input}
            maxLength={120}
          />
        </label>
        <label className="block">
          <span className={label}>Description (optional)</span>
          <textarea
            data-testid="create-rr-description"
            rows={3}
            value={details.description || ''}
            onChange={(e) => setDetails({ ...details, description: e.target.value })}
            placeholder="e.g. Casual mixer with pool play + a bracket final."
            className={`${input} resize-y`}
          />
        </label>
        <div>
          <div className={label}>Event flyer / poster (optional)</div>
          <div className="mt-1">
            <EventFlyerLocalPicker
              file={flyerFile}
              onChange={setFlyerFile}
              testIdPrefix="create-rr-flyer"
            />
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <div className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold">Event Setup</div>
        <div>
          <div className={label}>Type</div>
          <div className="mt-1 flex gap-2">
            {['singles', 'doubles'].map((t) => (
              <button key={t} type="button"
                data-testid={`create-rr-type-${t}`}
                onClick={() => setRrType(t)}
                className={`flex-1 py-2 rounded-lg border text-sm min-h-[44px] ${rrType === t ? 'bg-[#013523] text-white border-[#013523]' : 'border-[#e3dcd1] text-[#013523]'}`}>
                {t === 'singles' ? 'Singles' : 'Doubles'}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className={label}>Composition</div>
          <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { v: 'open',   label: 'Open' },
              { v: 'mens',   label: "Men's" },
              { v: 'womens', label: "Women's" },
              { v: 'mixed',  label: 'Mixed' },
            ].map((c) => {
              const disabled = rrType === 'singles' && c.v === 'mixed';
              return (
                <button key={c.v} type="button"
                  disabled={disabled}
                  onClick={() => setComposition(c.v)}
                  data-testid={`create-rr-comp-${c.v}`}
                  className={`py-2 rounded-lg border text-sm min-h-[44px] ${composition === c.v ? 'bg-[#013523] text-white border-[#013523]' : 'border-[#e3dcd1] text-[#013523]'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className={label}>Pools</span>
            <input
              data-testid="create-rr-pools"
              type="number" min="1" max="8" value={numPools}
              onChange={(e) => setNumPools(Number(e.target.value) || 1)}
              className={input}
            />
          </label>
          <label className="block">
            <span className={label}>{rrType === 'doubles' ? 'Max Teams (optional)' : 'Max Players (optional)'}</span>
            <input
              data-testid="create-rr-max"
              type="number" min="2" max="256" value={maxEntries}
              onChange={(e) => setMaxEntries(e.target.value)}
              placeholder={rrType === 'doubles' ? 'e.g. 20 teams' : 'e.g. 16 players'}
              className={input}
            />
            {rrType === 'doubles' && maxEntries && Number(maxEntries) > 0 && (
              <div className="text-[11px] text-[#6a766e] mt-1" data-testid="create-rr-max-hint">
                {maxEntries} teams · {Number(maxEntries) * 2} players maximum
              </div>
            )}
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm text-[#013523]">
          <input
            data-testid="create-rr-has-final"
            type="checkbox" checked={hasFinal}
            onChange={(e) => setHasFinal(e.target.checked)}
          />
          Play a Final between top pool finishers
        </label>
      </section>

      <section className="space-y-3">
        <div className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold">Date, Location &amp; Format</div>
        <EventDetailsFieldset value={details} onChange={setDetails} hideDescription />
      </section>

      <section className="space-y-3">
        <div className="text-[11px] uppercase tracking-wider text-[#013523] font-semibold">Event Manager</div>
        <ManagerSelectField
          value={managerProfileId}
          onChange={(pid) => setManagerProfileId(pid)}
          testIdPrefix="create-rr-mgr"
        />
      </section>
    </>
  );

  const actions = (
    <>
      <Button variant="outline" data-testid="create-rr-cancel" onClick={onClose} disabled={busy}>
        Cancel
      </Button>
      <Button data-testid="create-rr-submit" onClick={submit} disabled={busy || !name.trim()}
        className="bg-[#013523] text-white hover:bg-[#024029]">
        {busy ? 'Creating…' : 'Create round robin'}
      </Button>
    </>
  );

  if (embedded) {
    return (
      <div className="bg-white border border-[#e3dcd1] rounded-2xl overflow-hidden" data-testid="create-rr-embedded">
        <div className="px-5 pt-5 sm:px-6 sm:pt-6">
          <h2 className="text-lg font-semibold text-[#013523]">New Round Robin</h2>
          <p className="text-sm text-[#6a766e]">Fill in the event details. Flyer, dates, and description are all optional.</p>
        </div>
        <div className="px-5 py-4 sm:px-6 space-y-6">{sections}</div>
        <div className="flex justify-end gap-2 px-5 pb-5 sm:px-6">{actions}</div>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent data-testid="create-rr-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="pr-6">New Round Robin</DialogTitle>
          <DialogDescription>Fill in the event details. Flyer, dates, and description are all optional.</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-6">{sections}</DialogBody>
        <DialogFooter>{actions}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
